'use client'

import { useEffect, useRef } from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { ChevronDownIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

import { activeLocaleOption, type LocaleOption } from './locale-menu-config'

/** 4x3 artwork at navbar scale. */
const FLAG_WIDTH = 21
const FLAG_HEIGHT = 16

interface CountryFlagProps {
    option: LocaleOption
    className?: string
}

/**
 * A country's flag. Always decorative — every flag the picker renders sits beside
 * that country's name (or, on the trigger, beside a screen-reader-only one), so an
 * alt text here would only make assistive tech say the country twice.
 *
 * `unoptimized` is load-bearing, not a micro-optimization: `trailingSlash: true` makes
 * the image optimizer emit broken URLs, and there is no global `images.unoptimized` to
 * fall back on — only this per-call-site prop. Dropping it ships a broken flag. Every
 * other `<Image>` in this repo carries it for the same reason.
 */
export function CountryFlag({ option, className }: CountryFlagProps) {
    return (
        <Image
            unoptimized
            src={option.flag}
            alt=""
            width={FLAG_WIDTH}
            height={FLAG_HEIGHT}
            className={cn('rounded-[1px] border border-black/10', className)}
        />
    )
}

interface LocaleMenuProps {
    /** The countries to offer, from `buildLocaleOptions`. */
    options: LocaleOption[]
}

/**
 * Dismiss an open `<details>` on an outside press or Escape — the two things the
 * native element does not do for you, and the reason a plain `<details>` otherwise
 * feels broken next to a real menu: it stays open until you press the trigger again.
 *
 * **This is not the layer RD-3 warns about.** That trap was a *hover-open* listener
 * racing the primitive's own `pointerdown` toggle, which put opening out of phase and
 * made the next press close what the pointer's arrival had just opened. Nothing here
 * touches opening, and an outside press is by definition not on the trigger, so there
 * is no interaction to race.
 *
 * The element's own `open` attribute stays the single source of truth — no mirrored
 * React state — so the server still renders the whole panel and the country anchors
 * remain in the served markup (C-1). Listeners are attached only while the panel is
 * open, since a closed picker on every page of the site should not hold document-level
 * handlers.
 */
function useDismissOnOutsidePress(ref: React.RefObject<HTMLDetailsElement | null>) {
    useEffect(() => {
        const details = ref.current
        if (!details) {
            return
        }

        const close = () => {
            details.open = false
        }

        const onPointerDown = (event: PointerEvent) => {
            if (event.target instanceof Node && !details.contains(event.target)) {
                close()
            }
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close()
                // Focus would otherwise be left on a panel that no longer exists.
                details.querySelector('summary')?.focus()
            }
        }

        // Called as methods on `document`, not extracted into a variable — an
        // unbound `addEventListener` loses its receiver and throws.
        const listen = (add: boolean) => {
            if (add) {
                document.addEventListener('pointerdown', onPointerDown as EventListener)
                document.addEventListener('keydown', onKeyDown as EventListener)
                return
            }
            document.removeEventListener('pointerdown', onPointerDown as EventListener)
            document.removeEventListener('keydown', onKeyDown as EventListener)
        }

        // `toggle` fires for programmatic changes too, so closing above unbinds here.
        const onToggle = () => listen(details.open)

        details.addEventListener('toggle', onToggle)
        return () => {
            details.removeEventListener('toggle', onToggle)
            listen(false)
        }
    }, [ref])
}

/**
 * The header's country picker: the active country's flag, opening a list of the
 * countries nexus serves, each a link to the current page in that country's locale.
 *
 * **A native `<details>`, not the dropdown primitive.** The country links must be
 * anchors in the *served* HTML (C-1 / RD-1, the reason the primary menu's dropdowns
 * are CSS-revealed rather than interaction-injected), and the dropdown primitive
 * cannot satisfy that: its content is portalled, so it renders nothing at all
 * server-side and mounts only once the menu is opened. `<details>` keeps the whole
 * list in the markup while giving click-and-keyboard toggling, an announced expanded
 * state, and the chevron's rotation for free — no controlled state, and none of the
 * hover/press phase trap that RD-3 records. What it does not do is close on Escape or
 * an outside press (RD-10); every item navigates away, so the open panel is transient
 * in practice.
 *
 * Presentational and pathname-blind: the caller resolves the destinations, the same
 * way `AppLayout` builds the menu, logo and account configuration.
 */
export function LocaleMenu({ options }: LocaleMenuProps) {
    const t = useTranslations('navigation')
    const active = activeLocaleOption(options)
    const detailsRef = useRef<HTMLDetailsElement>(null)

    useDismissOnOutsidePress(detailsRef)

    // Fewer than two countries is not a choice; an absent active option would leave
    // the trigger blank. Either way the header shows no picker rather than a dead one.
    if (!active || options.length < 2) {
        return null
    }

    return (
        <details ref={detailsRef} className="group/locale relative">
            <summary
                // The purpose AND the current country, in one label on purpose: an
                // `aria-label` *replaces* name-from-content, so a visually-hidden
                // country name inside the control would never be announced (it would
                // still satisfy a `toHaveTextContent` assertion, which is why the
                // binding asserts the accessible name instead).
                aria-label={`${t('chooseCountry')} — ${active.countryName}`}
                className="flex cursor-pointer list-none items-center gap-1 rounded-md px-2 py-2 outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden"
            >
                <CountryFlag option={active} />
                <ChevronDownIcon
                    className="relative top-[1px] size-3 transition duration-300 group-open/locale:rotate-180"
                    aria-hidden="true"
                />
            </summary>
            <div className="absolute top-full right-0 z-50 mt-1.5 w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow">
                <ul className="grid list-none gap-0.5">
                    {options.map((option) => (
                        <li key={option.locale}>
                            <a
                                href={option.href}
                                hrefLang={option.locale}
                                aria-current={option.isActive ? 'true' : undefined}
                                className={cn(
                                    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none',
                                    option.isActive && 'font-semibold',
                                )}
                            >
                                <CountryFlag option={option} />
                                {option.countryName}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </details>
    )
}
